"use client";

import { useCallback, useSyncExternalStore } from "react";

const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
}

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * A string preference persisted in localStorage. The server snapshot is always the fallback,
 * so first render matches the server and the stored value applies right after hydration.
 */
export function usePref<T extends string>(
  key: string,
  fallback: T,
  allowed: readonly T[],
): [T, (value: T) => void] {
  const value = useSyncExternalStore(
    subscribe,
    () => {
      const stored = read(key);
      return allowed.includes(stored as T) ? (stored as T) : fallback;
    },
    () => fallback,
  );
  const set = useCallback(
    (next: T) => {
      try {
        window.localStorage.setItem(key, next);
      } catch {
        // Storage can be blocked; the preference then lasts until reload only if re-set.
      }
      listeners.forEach((listener) => listener());
    },
    [key],
  );
  return [value, set];
}
